<script setup lang="ts">
import { DialogRoot as Sheet, DialogTrigger as SheetTrigger } from "reka-ui";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { useRoute } from "vue-router";
import PlatformTooltip from "@/components/editor/PlatformTooltip.vue";
import Button from "@/components/ui/button/Button.vue";
import Separator from "@/components/ui/separator/Separator.vue";
import SheetContent from "@/components/ui/sheet/SheetContent.vue";
import { useTopBarStore } from "@/stores/topBar";
import { useWorkspacesStore } from "@/stores/workspaces";
import DoppleLogoIcon from "../../assets/dopple-logo-icon.svg";
import { IconMenu } from "../../assets/icons";
import BreadCrumbs from "./BreadCrumbs.vue";
import EditorMenuBar from "./EditorMenuBar.vue";
import ForwardBackArrows from "./ForwardBackArrows.vue";
import SideNavContent from "./SideNavContent.vue";
import TopBarUser from "./TopBarUser.vue";

// Sheet nav menu state
const topBarStore = useTopBarStore();

const tooltipRouteFileNames = {
	"Workspace Overview": "workspace-overview",
	"Project Overview": "project-overview",
	"Product Overview": "product-overview",
	Workspaces: "workspace-overview",
	"Projects List": "project-overview",
	"Products List": "product-overview",
} as Record<string, string>;

defineProps<{
	hasSidebar?: boolean;
}>();

const workspaces = useWorkspacesStore();

const route = useRoute();

const hasUnsavedChanges = computed(() => {
	if (route.params.orgName && route.params.workspaceName) {
		const ws = workspaces.workspace({
			owner: route.params.orgName as string,
			name: route.params.workspaceName as string,
		});

		if (!ws) {
			return false;
		}

		if (
			Object.keys(ws.treeModifications.additions).length ||
			Object.keys(ws.treeModifications.updations).length ||
			Object.keys(ws.treeModifications.deletions).length
		) {
			return true;
		}
	}

	return false;
});
</script>

<template>
	<div
		:class="
			twMerge(
				'h-12 bg-background border-b flex gap-4 items-center px-8 pl-2',
				!hasSidebar ? 'md:pl-2' : $route.meta.hideSideNav ? '' : 'md:pl-8',
				!$route.meta.authRequired
					? 'cursor-not-allowed pointer-events-none'
					: '',
			)
		"
	>
		<div class="flex gap-2 h-full items-center">
			<DoppleLogoIcon
				:class="
					twMerge(
						'ml-1 w-6 h-auto text-brand-500 dark:text-foreground',
						$route.meta.hideSideNav ? 'hidden md:inline' : 'inline md:hidden',
					)
				"
			/>
			<Separator
				orientation="vertical"
				:class="
					twMerge(
						'ml-1',
						$route.meta.hideSideNav ? 'hidden md:block' : 'block md:hidden',
					)
				"
			/>
			<Sheet
				:open="topBarStore.isSheetMenuOpen"
				@update:open="
					() => (topBarStore.isSheetMenuOpen = !topBarStore.isSheetMenuOpen)
				"
			>
				<SheetTrigger as-child>
					<Button
						variant="ghost"
						size="icon-sm"
						:class="
							$route.meta.showEditorMenuBar
								? 'flex'
								: $route.meta.hideSideNav
									? 'hidden md:flex'
									: 'flex md:hidden'
						"
					>
						<IconMenu class="w-6 h-6" />
					</Button>
				</SheetTrigger>
				<SheetContent side="left" class="w-64 p-0 flex flex-col gap-0">
					<SideNavContent />
				</SheetContent>
			</Sheet>
			<Separator
				orientation="vertical"
				:class="
					$route.meta.showEditorMenuBar
						? 'flex'
						: $route.meta.hideSideNav
							? 'hidden md:flex'
							: 'flex md:hidden'
				"
			/>
			<EditorMenuBar />
			<Separator
				orientation="vertical"
				v-if="$route.meta.showEditorMenuBar"
				:class="$route.meta.hideSideNav ? 'hidden md:block' : 'block md:hidden'"
			/>
			<ForwardBackArrows />
			<Separator orientation="vertical" />
		</div>
		<BreadCrumbs class="hidden sm:block" />
		<div
			class="flex-grow text-yellow-200 text-sm text-left"
			v-if="hasUnsavedChanges"
		>
			(Unsaved Local Changes in Workspace)
		</div>
		<div class="flex gap-4 ml-auto" v-if="$route.meta.authRequired">
			<PlatformTooltip
				:key="$route.name"
				:file-name="tooltipRouteFileNames[$route.name as string]"
				v-if="
					Object.keys(tooltipRouteFileNames).includes($route.name as string)
				"
			/>
			<TopBarUser />
		</div>
	</div>
</template>
